/* Printable one-page "Timekeeping Responsibilities" handout (Sep 2026).
   Used by the Timekeeping & Payroll manual tab — admins print it for staff. */

export const RESPONSIBILITIES = {
  worker: {
    title: 'Workers',
    items: [
      'Clock in when you start and clock out when you finish — every shift, every day (app, Site QR, kiosk PIN or squad punch).',
      'Log your breaks. If your company treats breaks as unpaid, answer the meal-break question honestly at clock-out.',
      'Open My Time → "Needs review" after each shift (or at least before the pay-period cut-off). Check the times, then tap "Certify & submit".',
      'Something wrong? Do NOT certify — tap "Request a correction" and say what happened. Missing a whole shift? Use "Report missing time".',
      'If a supervisor returns a shift to you, read the reason, fix or explain, and re-certify.',
      'Forgot to clock out? Tell your supervisor the same day so the time is fixed before payroll.',
    ],
  },
  squad: {
    title: 'Squad leaders',
    items: [
      'Punch your squad in and out from My Day — you are certifying they were there, so only punch people who are.',
      'Each squad member still reviews and certifies their own shifts in My Time.',
      'Report absences or early departures to your supervisor the same day.',
    ],
  },
  supervisor: {
    title: 'Supervisors',
    items: [
      'Watch the team status daily: open shifts, missed punches, auto clock-outs. Nudge no-shows.',
      'Pending Approvals → review certified shifts; Approve (or Approve all) when the hours look right.',
      'Return a shift with a clear reason when something is off — the worker fixes and re-certifies.',
      'Review correction (amendment) requests promptly — approving one creates the corrected time entry.',
      'Clear your team\u2019s time exceptions (overlaps, long shifts, geofence flags) before the payroll cut-off.',
      'Remind staff who have not certified before the cut-off. For kiosk crews without phones, use Pending Approvals → Not Certified → "Certify for worker" (reason required).',
    ],
  },
  admin: {
    title: 'Administrators (payroll)',
    items: [
      'Payroll Review → pick the pay period and watch readiness climb to 100%.',
      'Approve supervisor-approved shifts (Approve / Approve all). Flag or send back anything questionable.',
      'After the cut-off, approve uncertified or unreviewed shifts with an override (reason required, worker notified, audit-logged).',
      'Fix wrong times or add missing shifts in Reports → Payroll Workbench — every change needs a reason and keeps the original.',
      'Lock the pay period (below 100% readiness = override reason). Locked time cannot be edited.',
      'Push hours to QuickBooks or download the payroll CSV, then Mark exported → Paid.',
      'Set the certify / approve / lock deadlines in Settings → Payroll Cut-offs and check Payroll Review → Overrides for repeat non-certifiers.',
    ],
  },
};

export const STATUS_CHAIN = [
  { key: 'open', label: 'Not submitted', who: 'Worker', hint: 'Shift closed, waiting for the worker to review & certify' },
  { key: 'submitted', label: 'Awaiting supervisor', who: 'Supervisor', hint: 'Worker certified — supervisor approves or returns' },
  { key: 'sup_approved', label: 'Awaiting payroll', who: 'Admin', hint: 'Supervisor approved — payroll approves, flags, or sends back' },
  { key: 'payroll_approved', label: 'Payroll approved', who: 'Admin', hint: 'Ready to lock & pay (also the result of an admin override)' },
  { key: 'locked', label: 'Locked', who: 'Admin', hint: 'Pay period locked — no more edits' },
  { key: 'paid', label: 'Paid', who: 'System', hint: 'Exported to QuickBooks / CSV and marked as paid' },
];

export function buildTimekeepingHandoutHtml(companyName) {
  const col = (r) => `<div class="col"><h2>${r.title}</h2><ol>${r.items.map((i) => `<li>${i}</li>`).join('')}</ol></div>`;
  const chain = STATUS_CHAIN.map((s) => `<span class="pill">${s.label}<small>${s.who}</small></span>`).join('<span class="arrow">→</span>');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Timekeeping Responsibilities — HinesTime</title>
<style>
  @page { margin: 12mm; size: landscape; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; }
  .head { display: flex; align-items: center; gap: 10px; border-bottom: 3px solid #1E88E5; padding-bottom: 8px; }
  .logo { width: 32px; height: 32px; border-radius: 8px; background: #1E88E5; color: #fff; font-weight: 800; font-size: 17px; display: flex; align-items: center; justify-content: center; }
  h1 { font-size: 18px; margin: 0; } .v { color: #6B7280; font-size: 11px; margin-top: 2px; }
  .chain { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; margin: 10px 0 12px; }
  .pill { display: inline-flex; flex-direction: column; align-items: center; border: 1px solid #1E88E5; border-radius: 999px; padding: 3px 10px; font-size: 10.5px; font-weight: 700; color: #1E3A8A; }
  .pill small { font-weight: 500; color: #6B7280; font-size: 9px; }
  .arrow { color: #9CA3AF; font-size: 12px; }
  .cols { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
  .col { border: 1px solid #E5E7EB; border-radius: 10px; padding: 8px 10px; }
  h2 { font-size: 12px; text-transform: uppercase; letter-spacing: .06em; color: #1E88E5; margin: 0 0 6px; }
  ol { margin: 0; padding-left: 16px; font-size: 10.5px; line-height: 1.4; } li { margin-bottom: 4px; }
  .foot { margin-top: 10px; padding-top: 6px; border-top: 1px solid #E5E7EB; font-size: 9.5px; color: #6B7280; }
</style></head><body>
  <div class="head"><div class="logo">H</div><div><h1>Timekeeping Responsibilities${companyName ? ` — ${companyName}` : ''}</h1><div class="v">Who does what so everyone is paid correctly and on time · HinesTime</div></div></div>
  <div class="chain">${chain}</div>
  <div class="cols">${col(RESPONSIBILITIES.worker)}${col(RESPONSIBILITIES.squad)}${col(RESPONSIBILITIES.supervisor)}${col(RESPONSIBILITIES.admin)}</div>
  <div class="foot">Full guide: HinesTime → Help &amp; Manual → "Timekeeping &amp; Payroll" tab (or /user-manual?tab=payroll). Certifying is strongly recommended; administrators may approve uncertified time with a documented reason so nobody misses a pay cheque.</div>
</body></html>`;
}
